import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { Layout } from '../components/Layout';

interface PriceAdjustment {
  id: number;
  start_date: string;
  end_date: string;
  adjustment_rate: number;
  name: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface PriceAdjustmentForm {
  start_date: string;
  end_date: string;
  adjustment_rate: number;
  name: string;
  is_active: boolean;
}

export const PriceAdjustmentsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<PriceAdjustmentForm>({
    start_date: '',
    end_date: '',
    adjustment_rate: 1.0,
    name: '',
    is_active: true,
  });

  // Fetch price adjustments
  const { data: adjustments = [], isLoading } = useQuery<PriceAdjustment[]>({
    queryKey: ['price-adjustments'],
    queryFn: async () => {
      const response = await apiClient.get('/price-adjustments');
      return response.data;
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: PriceAdjustmentForm) => {
      const response = await apiClient.post('/price-adjustments', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-adjustments'] });
      setShowModal(false);
      resetForm();
      alert('가격 할증이 추가되었습니다.');
    },
    onError: (error: any) => {
      alert(`추가 실패: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<PriceAdjustmentForm> }) => {
      const response = await apiClient.put(`/price-adjustments/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-adjustments'] });
      setShowModal(false);
      setEditingId(null);
      resetForm();
      alert('가격 할증이 수정되었습니다.');
    },
    onError: (error: any) => {
      alert(`수정 실패: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/price-adjustments/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-adjustments'] });
      alert('가격 할증이 삭제되었습니다.');
    },
    onError: (error: any) => {
      alert(`삭제 실패: ${error.response?.data?.detail || error.message}`);
    },
  });

  const resetForm = () => {
    setFormData({
      start_date: '',
      end_date: '',
      adjustment_rate: 1.0,
      name: '',
      is_active: true,
    });
    setEditingId(null);
  };

  const handleOpenModal = (adjustment?: PriceAdjustment) => {
    if (adjustment) {
      setEditingId(adjustment.id);
      setFormData({
        start_date: adjustment.start_date,
        end_date: adjustment.end_date,
        adjustment_rate: adjustment.adjustment_rate,
        name: adjustment.name || '',
        is_active: adjustment.is_active,
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (editingId) {
      updateMutation.mutate({
        id: editingId,
        data: {
          start_date: formData.start_date,
          end_date: formData.end_date,
          adjustment_rate: formData.adjustment_rate,
          name: formData.name,
          is_active: formData.is_active,
        },
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('정말로 이 할증을 삭제하시겠습니까?')) {
      deleteMutation.mutate(id);
    }
  };

  const getAdjustmentRateDisplay = (rate: number) => {
    const percentage = Math.round(rate * 100);
    if (rate > 1) {
      return `+${percentage - 100}% (${percentage}%)`;
    } else if (rate < 1) {
      return `-${100 - percentage}% (${percentage}%)`;
    } else {
      return '정상가 (100%)';
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-600">로딩 중...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-2 py-4">
        {/* Header */}
        <div className="mb-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">날짜별 가격 할증 관리</h1>
            <p className="text-sm text-gray-600">
              특정 날짜에 대한 차량 렌탈 요금 할증률을 설정합니다.
            </p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            할증 추가
          </button>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">할증률 안내</h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc pl-5 space-y-1">
                  <li>할증률 1.0 = 정상가 (100%)</li>
                  <li>할증률 1.5 = 50% 할증 (150%)</li>
                  <li>할증률 0.8 = 20% 할인 (80%)</li>
                  <li>연휴, 성수기 등 특정 날짜의 요금을 조정할 수 있습니다</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {adjustments.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              등록된 가격 할증이 없습니다.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">기간</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">할증 사유</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">할증률</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">작업</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {adjustments.map((adjustment) => (
                    <tr key={adjustment.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div>
                          {new Date(adjustment.start_date).toLocaleDateString('ko-KR', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                          })}
                          {' ~ '}
                          {new Date(adjustment.end_date).toLocaleDateString('ko-KR', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                          })}
                        </div>
                        <div className="text-xs text-gray-500">
                          ({Math.ceil((new Date(adjustment.end_date).getTime() - new Date(adjustment.start_date).getTime()) / (1000 * 60 * 60 * 24)) + 1}일)
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {adjustment.name || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`font-medium ${adjustment.adjustment_rate > 1 ? 'text-red-600' : adjustment.adjustment_rate < 1 ? 'text-green-600' : 'text-gray-900'}`}>
                          {getAdjustmentRateDisplay(adjustment.adjustment_rate)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${adjustment.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {adjustment.is_active ? '활성' : '비활성'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right space-x-2">
                        <button
                          onClick={() => handleOpenModal(adjustment)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          수정
                        </button>
                        <button
                          onClick={() => handleDelete(adjustment.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h2 className="text-xl font-bold mb-4">
                {editingId ? '할증 수정' : '할증 추가'}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      시작 날짜 *
                    </label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500"
                      required
                      disabled={!!editingId}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      종료 날짜 *
                    </label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500"
                      required
                      disabled={!!editingId}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      할증률 * (0.1 ~ 10.0)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.1"
                      max="10"
                      value={formData.adjustment_rate}
                      onChange={(e) => setFormData({ ...formData, adjustment_rate: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      현재: {getAdjustmentRateDisplay(formData.adjustment_rate)}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      할증 사유
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="예: 추석 연휴, 설날"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                      활성화
                    </label>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    disabled={createMutation.isPending || updateMutation.isPending}
                  >
                    {createMutation.isPending || updateMutation.isPending ? '처리 중...' : (editingId ? '수정' : '추가')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
